import {
	prepareWriteContract,
	writeContract,
	type SendTransactionResult,
	erc20ABI,
	sendTransaction,
	prepareSendTransaction
} from '@wagmi/core';
import { encodeFunctionData } from 'viem';
import { ZERO_ADDRESS } from '../addresses';
import abi from '../abi';
import { estimateGasLimits, estimateGasLimitsRaw } from '../estimateGaslimit';
import {
	buildAddAuthorityCallData,
	buildRemoveAuthorityCallData,
	buildWithdrawTokenFromSCWCallData
} from '../builders/scWallet';
import type { BigNumber } from 'ethers';
import { isItBedRockShutDownTime, throwError } from '$lib/home/util';
import { getMaxFeeAndPriorityFee } from '../utils/gas';

export async function createSmartContractWallet(
	walletAddress: `0x${string}`,
	SC_WALLET_INDEX: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();
	const data = encodeFunctionData({
		abi: abi.scWalletIndex,
		functionName: 'build',
		args: [walletAddress, 1, ZERO_ADDRESS]
	});
	const gas = await estimateGasLimitsRaw(SC_WALLET_INDEX, data, 0n);
	const config = await prepareSendTransaction({
		to: SC_WALLET_INDEX,
		data,
		gas,
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await sendTransaction(config);
	return txData;
}

export async function addETHFundsToSCW(
	smartContractWalletAddress: `0x${string}`,
	addFundsTokenAmount: BigNumber
): Promise<SendTransactionResult> {
	const amount = addFundsTokenAmount.toBigInt();
	const gas = await estimateGasLimitsRaw(smartContractWalletAddress, '0x', amount);
	const config = await prepareSendTransaction({
		to: smartContractWalletAddress as `0x${string}`,
		value: amount,
		gas
	});

	const txData: SendTransactionResult = await sendTransaction(config);
	return txData;
}

export async function addFundsToSCW(
	tokenAddress: `0x${string}`,
	smartContractWalletAddress: `0x${string}`,
	addFundsTokenAmount: BigNumber
): Promise<SendTransactionResult> {
	if (isItBedRockShutDownTime()) {
		throwError();
	}

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();
	const data = encodeFunctionData({
		abi: erc20ABI,
		functionName: 'transfer',
		args: [smartContractWalletAddress, addFundsTokenAmount.toBigInt()]
	});
	const gas = await estimateGasLimitsRaw(tokenAddress, data, 0n);
	const config = await prepareSendTransaction({
		to: tokenAddress,
		data,
		gas,
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await sendTransaction(config);
	return txData;
}

export async function withdrawTokenFromSCW(
	tokenAddress: string,
	smartContractWalletAddress: `0x${string}`,
	withdrawFundsTokenAmount: BigNumber,
	walletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const withdrawTokenFromSCWCallData = await buildWithdrawTokenFromSCWCallData(
		tokenAddress,
		withdrawFundsTokenAmount,
		walletAddress
	);

	targetArray.push(...withdrawTokenFromSCWCallData._targetArray);
	callDataArray.push(...withdrawTokenFromSCWCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);
	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();

	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function addAuthority(
	smartContractWalletAddress: `0x${string}`,
	walletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const addAuthorityToSCWCallData = await buildAddAuthorityCallData(walletAddress);

	targetArray.push(...addAuthorityToSCWCallData._targetArray);
	callDataArray.push(...addAuthorityToSCWCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();

	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function removeAuthority(
	smartContractWalletAddress: `0x${string}`,
	walletAddress: `0x${string}`
) {
	const _smartContractWalletAddress = smartContractWalletAddress;
	const functionName: string = 'cast';
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const removeAuthorityToSCWCallData = await buildRemoveAuthorityCallData(walletAddress);

	targetArray.push(...removeAuthorityToSCWCallData._targetArray);
	callDataArray.push(...removeAuthorityToSCWCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();

	const config = await prepareWriteContract({
		address: _smartContractWalletAddress as `0x${string}`,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}
